<script lang="ts" setup>
defineProps<{
  previewImageUrl?: string | null
  senderName?: string
}>()
</script>

<template>
  <div class="cover">
    <img v-if="previewImageUrl" :src="previewImageUrl" alt="" class="cover-image">
    <template v-else>
      <h1>A gift for you</h1>
      <p v-if="senderName">From {{ senderName }}</p>
    </template>
  </div>
</template>

<style scoped lang="scss">
// Starting dimensions for the cover in its CLOSED state, matched to the
// envelope card's rendered visual size. On mobile this includes the
// --scene-scale factor (set on the page root) so the handoff has no size
// jump. The card in the envelope scene now sits at scene center (no
// translateY offset), so the cover starts at scene center too.
$start-width: calc(var(--card-width) * var(--scene-scale));
$start-height: calc(var(--card-height) * var(--scene-scale));

// The cover sits on top of the page stack while we're on the first page and
// is gesture-driven by --cover-progress (0 closed → 1 fully open). It both
// rotates around its left edge AND expands toward fullscreen as it opens.
.cover {
  backface-visibility: hidden;
  background: var(--card-color);

  // Mounts with the shadow already at full value (no fade-in) — matches the
  // shadow the envelope card had at handoff, so there's no flicker.
  box-shadow: var(--card-envelope-box-shadow);
  height: calc(#{$start-height} + (100dvh - #{$start-height}) * var(--cover-progress));
  isolation: isolate;
  left: 50%;
  overflow: hidden;
  position: absolute;

  // Lift on `top` matches `.reading-page-flipping` in ReadingStage — see
  // ReadingStage's `.snapping` transition rule for why this is on `top` instead
  // of folded into the `transform` calc. Centers between the narration + navbar
  // reserves (narration defaults to 0 outside the showcase).
  top: calc(50% + (var(--narration-reserved-space, 0px) - var(--nav-bar-reserved-space, 0px)) / 2 * (1 - var(--cover-progress)));
  transform:
    translate3d(-50%, -50%, 0)
    rotateY(calc(var(--cover-progress) * -180deg));
  transform-origin: left center;
  width: calc(#{$start-width} + (100vw - #{$start-width}) * var(--cover-progress));
  will-change: top, transform, width, height;
  z-index: 10;
}

.cover-image {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.cover h1 {
  color: var(--text-dark);
  font-size: 1.75rem;
  font-weight: normal;
  letter-spacing: 1px;
  margin-bottom: 15px;
  text-align: center;
}

.cover p {
  color: var(--text-light);
  font-size: 0.875rem;
  text-align: center;
}
</style>
